import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ArrowLeft, Mail, Calendar, CheckCircle, XCircle } from 'lucide-react'
import { format } from 'date-fns'
import { LoadingScreen } from '@/components/common/feedback/LoadingScreen'
import {
  useArtist,
  useArtistCommunications,
  useSendEmailToArtist,
  useUpdateArtistEmailPreferences,
  useArtistGenome,
  useUpdateArtistGenome,
} from '../hooks/useArtistAdmin'
import { CommunicationHistory } from '../components/CommunicationHistory'
import { EmailStatsCard } from '../components/EmailStatsCard'
import { EmailPreferencesForm } from '../components/EmailPreferencesForm'
import { CreativeGenomePanel } from '../components/CreativeGenomePanel'
import { SendEmailDialog } from '../components/SendEmailDialog'
import type { EmailPreferences, CreativeGenome } from '../types'

export function ArtistDetailPage() {
  const { artistId } = useParams<{ artistId: string }>()
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)

  const { data: artist, isLoading: artistLoading } = useArtist(artistId ? parseInt(artistId) : null)
  const { data: communications = [], isLoading: communicationsLoading } = useArtistCommunications(
    artistId ? parseInt(artistId) : null
  )

  const { data: genomeData, isLoading: genomeLoading } = useArtistGenome(
    artistId ? parseInt(artistId) : null
  )

  const sendEmail = useSendEmailToArtist()
  const updatePreferences = useUpdateArtistEmailPreferences()
  const updateGenome = useUpdateArtistGenome()

  const handleSaveGenome = async (genome: CreativeGenome) => {
    if (artistId) {
      await updateGenome.mutateAsync({ artistId: parseInt(artistId), genome })
    }
  }

  const handleSendEmail = async (data: { subject: string; htmlContent: string; textContent: string }) => {
    if (artistId) {
      await sendEmail.mutateAsync({
        artistId: parseInt(artistId),
        payload: data,
      })
    }
  }

  const handleSavePreferences = async (preferences: Partial<Omit<EmailPreferences, 'id'>>) => {
    if (artistId) {
      await updatePreferences.mutateAsync({
        artistId: parseInt(artistId),
        preferences,
      })
    }
  }

  if (artistLoading) {
    return <LoadingScreen />
  }

  if (!artist) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <h2 className="text-xl font-semibold mb-2">Artist Not Found</h2>
        <p className="text-muted-foreground mb-4">The requested artist could not be found.</p>
        <Link to="/app/artists">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Artists
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/app/artists">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-serif font-semibold">
              {artist.artisticName || `${artist.firstName} ${artist.lastName}`}
            </h1>
            {artist.verified ? (
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                <CheckCircle className="mr-1 h-3 w-3" />
                Verified
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                <XCircle className="mr-1 h-3 w-3" />
                Unverified
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">{artist.email}</p>
        </div>
        <Button onClick={() => setEmailDialogOpen(true)}>
          <Mail className="mr-2 h-4 w-4" />
          Send Email
        </Button>
      </div>

      {/* Artist Info Card */}
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm text-muted-foreground">Name</p>
              <p className="font-medium">{artist.firstName} {artist.lastName}</p>
            </div>
            {artist.artisticName && (
              <div>
                <p className="text-sm text-muted-foreground">Artist Name</p>
                <p className="font-medium">{artist.artisticName}</p>
              </div>
            )}
            {artist.bio && (
              <div>
                <p className="text-sm text-muted-foreground">Bio</p>
                <p className="text-sm">{artist.bio}</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Activity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                Joined {format(new Date(artist.dateCreate), 'MMM d, yyyy')}
              </span>
            </div>
            {artist.dateLastLogin && (
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">
                  Last active {format(new Date(artist.dateLastLogin), 'MMM d, yyyy')}
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Email Stats</CardTitle>
          </CardHeader>
          <CardContent>
            {artist.emailStats && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-2xl font-bold">{artist.emailStats.totalSent}</p>
                  <p className="text-xs text-muted-foreground">Sent</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{artist.emailStats.opened}</p>
                  <p className="text-xs text-muted-foreground">Opened</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">{artist.emailStats.clicked}</p>
                  <p className="text-xs text-muted-foreground">Clicked</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Stats Card */}
      {artist.emailStats && (
        <EmailStatsCard stats={artist.emailStats} title="Email Performance" />
      )}

      {/* Tabs */}
      <Tabs defaultValue="history">
        <TabsList>
          <TabsTrigger value="history">Communication History</TabsTrigger>
          <TabsTrigger value="preferences">Email Preferences</TabsTrigger>
          <TabsTrigger value="genome">Creative Genome</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Communication History</CardTitle>
              <CardDescription>All emails sent to this artist</CardDescription>
            </CardHeader>
            <CardContent>
              <CommunicationHistory
                emails={communications}
                isLoading={communicationsLoading}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preferences" className="mt-4">
          <EmailPreferencesForm
            preferences={artist.emailPreferences}
            onSave={handleSavePreferences}
            isLoading={updatePreferences.isPending}
          />
        </TabsContent>

        <TabsContent value="genome" className="mt-4">
          <CreativeGenomePanel
            genome={genomeData?.data ?? null}
            updatedAt={genomeData?.updatedAt ?? null}
            isLoading={genomeLoading}
            isSaving={updateGenome.isPending}
            onSave={handleSaveGenome}
          />
        </TabsContent>
      </Tabs>

      {/* Email Dialog */}
      <SendEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        recipientName={artist.artisticName || `${artist.firstName} ${artist.lastName}`}
        onSend={handleSendEmail}
        isLoading={sendEmail.isPending}
      />
    </div>
  )
}
